<template>
  <div class="toplist">
    <div
      v-for="(row, index) in rows"
      :key="row.id"
      class="toplist-item"
      @mouseenter="() => onMouseEnter(row.id)"
      @mouseleave="removeLastHoveredItem"
      @click="() => onMouseClick(row.id)"
    >
      <div class="item-label">
        <TextBase
          :text="getEntityName(row.id)"
          :weight="'bold'"
          :type="'label'"
          :style="{ 'line-height': 'unset' }"
        />
        <TextBase
          :text="''"
          :weight="'bold'"
          :type="'label'"
          :color="'#767676'"
          :style="{ 'line-height': 'unset' }"
        />
      </div>
      <div class="item-chart">
        <ChartBase
          height="16px"
          :style="{
            width: '100%',
            height: '16px',
            color: '#282828'
          }"
          :paddingOverride="{ top: 20, bottom: 20, left: 0, right: 0 }"
          :components="rowComponents[index]"
        />
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { computed } from 'vue'
import { Limits } from './ChartComponent/chart/limits'
import { FormatOptions } from '../formatter'
import TextBase from '@/design-system/atoms/TextBase.vue'
import { Bar, createBars } from './ChartComponent/chart/bars'
import { AreaHoverHighlight, createAreaHightlights } from './ChartComponent/chart/area-highlight'
import { createTooltip } from './ChartComponent/chart/tooltip'
import { ChartComponent } from './ChartComponent'
import ChartBase from './ChartComponent/ChartBase.vue'
import { useI18n } from 'vue-i18n'
import { useHoverStore } from '@/pinia/hover'
import { useEntityLookup } from '@/composables/useEntityLookup'
import { MeasurementPoint } from '../measurements/contracts'
import useNavigateTo from '@/composables/useNavigateTo'
import { BarConfiguration } from './toplist-types'

type Props = {
  data: MeasurementPoint[]
  format: FormatOptions
  locale: string
  barOptions: BarConfiguration[]
}

const props = defineProps<Props>()

const { navigateTo } = useNavigateTo()
const { addHoveredItem, removeLastHoveredItem } = useHoverStore()
const { findEntity, getEntityDisplayString } = useEntityLookup()

function onMouseEnter(id: string) {
  const entity = findEntity(id)
  if (entity) {
    addHoveredItem(entity.id, { color: '#fadb14' })
  }
}

async function onMouseClick(id: string) {
  await navigateTo(id)
}

function getEntityName(id: string) {
  return getEntityDisplayString(id, true) ?? id
}

const limits: Limits = {
  x: [0, 1],
  y: [0, 1]
}

const max = computed(() => {
  if (!props.data || props.data.length === 0) return 0
  return props.data
    .map((m) => (Object.values(m.fields) as number[]).reduce((a, b) => a + b, 0))
    .reduce((a, b) => Math.max(a, b), -Infinity)
})

const rows = computed(() => {
  if (!props.data || props.data.length === 0) return []

  const globalMaxValue = max.value

  return props.data.map((m) => {
    const id = m.path?.split('.')?.slice(-1)[0] ?? '-'
    const fieldValues = Object.values(m.fields) as number[]
    const sumForRow = fieldValues.reduce((a, b) => a + b, 0)

    let proportionFilled: number
    if (globalMaxValue <= 0) {
      proportionFilled = 0
    } else {
      proportionFilled = sumForRow / globalMaxValue
    }

    proportionFilled = Math.max(0, Math.min(1, proportionFilled))

    const proportionRemainder = 1 - proportionFilled

    return {
      id: id,

      normalizedProportions: [proportionFilled, proportionRemainder],

      originalSegmentValues: [sumForRow, Math.max(0, globalMaxValue - sumForRow)],
      originalTotalForRow: sumForRow
    }
  })
})

const rowComponents = computed<ChartComponent[][]>(() => {
  const locale = useI18n().locale.value

  const barsForEachRow: Bar[][] = rows.value.map((row) => {
    return row.normalizedProportions.map((segmentProportion, segmentIndex) => {
      const barConfig = props.barOptions[segmentIndex]

      if (!barConfig) {
        console.warn(
          `Toplist: Missing bar configuration for segment index ${segmentIndex}. Ensure props.barOptions has at least two entries.`
        )

        return { x: 0.5, height: segmentProportion, color: 'transparent' } as Bar
      }

      const bar: Bar = {
        ...barConfig,
        x: 0.5,
        height: segmentProportion,
        label: barConfig.label?.[locale]
      }
      return bar
    })
  })

  const makeHighlights = (proportions: number[], originalSegmentValues: number[]) => {
    let temp: AreaHoverHighlight[] = []
    let accumulatedProportion = 0

    for (let i = 0; i < proportions.length; i++) {
      const segmentProportion = proportions[i]
      const barOptionForSegment = props.barOptions[i]

      if (!barOptionForSegment) continue

      const currentSegmentStartX = accumulatedProportion
      const currentSegmentEndX = accumulatedProportion + segmentProportion

      if (barOptionForSegment.tooltip !== false && segmentProportion > 1e-6) {
        const actualValueForSegment = originalSegmentValues[i]

        const highlight: AreaHoverHighlight = {
          label: barOptionForSegment.label?.[locale],
          color: barOptionForSegment.color,
          pattern: barOptionForSegment.pattern,
          path: [
            [currentSegmentStartX, 0],
            [currentSegmentEndX, 0],
            [currentSegmentEndX, 1],
            [currentSegmentStartX, 1]
          ],
          value: [0, actualValueForSegment],
          visible: false
        }
        temp.push(highlight)
      }
      accumulatedProportion = currentSegmentEndX
    }
    return temp
  }

  const finalBarComponents = barsForEachRow.map((singleRowSegmentBars, rowIndex) => {
    const currentRowData = rows.value[rowIndex]
    return [
      ...createTooltip(limits, props.format),
      ...createBars([singleRowSegmentBars], 1, 'horizontal', limits),
      ...createAreaHightlights(
        makeHighlights(currentRowData.normalizedProportions, currentRowData.originalSegmentValues),
        limits
      )
    ]
  })
  return finalBarComponents
})
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/mixins.scss';
.toplist {
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 16px;
  gap: 16px;
  max-height: 420px;
  min-height: 70px;
  overflow-y: auto;
  @include mixins.scrollbar-vertical;
}

.toplist-item {
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
}

.item-label {
  display: flex;
  flex-direction: row;
  gap: 5px;
  width: fit-content;
  border-bottom: 1px solid transparent;

  &:hover {
    cursor: pointer;
    border-bottom-color: currentColor;
  }
}
</style>
